
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Business } from "@/types";
import BusinessImage from "@/components/common/BusinessImage";

interface BusinessCardProps {
  business: Business;
}

const BusinessCard: React.FC<BusinessCardProps> = ({ business }) => {
  const [isLoading, setIsLoading] = useState(true);

  return (
    <Link to={`/business/${business.id}`} className="block h-full">
      <Card className="overflow-hidden h-full transition-transform hover:scale-105 cursor-pointer">
        <div className="relative h-40 sm:h-48 overflow-hidden bg-muted">
          <BusinessImage
            imageUrls={business.media_urls}
            fallbackUrl={business.image_url}
            placeholderSrc="/images/business-placeholder.svg"
            altText={business.name}
            className="object-cover w-full h-full transition-opacity duration-300"
            onLoad={() => setIsLoading(false)}
            onError={() => setIsLoading(false)}
          />
        </div>
        <CardContent className="pt-4 p-3 sm:p-4">
          <div className="flex items-center justify-between mb-2">
            <Badge className="bg-eco-100 text-eco-800 hover:bg-eco-200 text-xs whitespace-nowrap overflow-hidden text-ellipsis">
              {business.category || "Other"}
            </Badge>
          </div>
          <h3 className="font-semibold text-base sm:text-lg mb-1 line-clamp-1">{business.name}</h3>
          <div className="flex items-center text-muted-foreground text-xs sm:text-sm mb-2">
            <MapPin className="h-3 w-3 sm:h-4 sm:w-4 mr-1 flex-shrink-0" />
            <span className="truncate">
              {[business.city, business.county || business.state].filter(Boolean).join(", ")}
            </span>
          </div>
          {business.description && (
            <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2">
              {business.description}
            </p>
          )}
        </CardContent>
        <CardFooter className="pt-0 pb-3 sm:pb-4 px-3 sm:px-4">
          {business.tags && business.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {business.tags.slice(0, 2).map((tag) => (
                <Badge key={tag} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </CardFooter>
      </Card>
    </Link>
  );
};

export default BusinessCard;
